import { createContext, useContext, useEffect, useCallback, useState, useRef } from "react";
import { activityApi } from "@/lib/api";

const NotificationContext = createContext({
  notifications: [],
  unreadCount: 0,
  refresh: () => {},
  markAllRead: () => {},
  markOneRead: () => {},
});

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const timer = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const [nRes, cRes] = await Promise.all([
        activityApi.getNotifications(30),
        activityApi.getUnreadCount(),
      ]);
      setNotifications(Array.isArray(nRes.data) ? nRes.data : []);
      setUnreadCount(cRes.data?.unread || 0);
    } catch (e) {
      console.debug("Benachrichtigungen konnten nicht geladen werden:", e);
    }
  }, []);

  const markAllRead = useCallback(async () => {
    setUnreadCount(0);
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      await activityApi.markAllRead();
    } catch (e) {
      console.debug("Als-gelesen-Markierung fehlgeschlagen:", e);
    }
  }, []);

  const markOneRead = useCallback(async (id) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnreadCount((c) => Math.max(0, c - 1));
    try {
      await activityApi.markOneRead(id);
    } catch (e) {
      console.debug("Als-gelesen-Markierung fehlgeschlagen:", e);
    }
  }, []);

  useEffect(() => {
    refresh();
    timer.current = setInterval(refresh, 25000);
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => {
      if (timer.current) clearInterval(timer.current);
      window.removeEventListener("focus", onFocus);
    };
  }, [refresh]);

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, refresh, markAllRead, markOneRead }}>
      {children}
    </NotificationContext.Provider>
  );
};
